import type {ToolCallMessagePartComponent} from '@assistant-ui/react';
import {Icon} from '@wandb/components';
import React, {useState} from 'react';

type NetworkAccessArgs = {reason?: string; domains?: string[]};

/**
 * NetworkAccessToolUI renders a request_network_access call as a compact
 * historical indicator, expandable to show the reason and requested domains.
 */
export const NetworkAccessToolUI: ToolCallMessagePartComponent<
  NetworkAccessArgs
> = ({args}) => {
  const [expanded, setExpanded] = useState(false);
  const {reason, domains} = args ?? {};
  const domainList = Array.isArray(domains) ? domains : [];
  const summary =
    domainList.length > 0
      ? `Requested network access to ${domainList.join(', ')}`
      : 'Requested network access';
  const canExpand = !!reason || domainList.length > 0;

  return (
    <div className="flex flex-col gap-8 py-4">
      <button
        type="button"
        disabled={!canExpand}
        onClick={() => setExpanded(e => !e)}
        className="flex w-full items-center gap-8 -mx-6 px-6 py-4 text-left transition-colors rounded-md bg-transparent hover:bg-moon-100 cursor-pointer text-moon-800 disabled:cursor-default disabled:hover:bg-transparent">
        <span className="inline-flex shrink-0">
          <Icon icon="lock-open" size="sm" color="sienna" />
        </span>
        <span className="text-left leading-[21px] font-semibold text-sm">
          {summary}
        </span>
        <span className="flex-1" />
        {canExpand && (
          <span
            className={`inline-flex shrink-0 text-moon-450 transition-[rotate] duration-[220ms] ${
              expanded ? 'rotate-90' : 'rotate-0'
            }`}>
            <Icon icon="chevron-next" size="sm" />
          </span>
        )}
      </button>
      {expanded && (
        <div className="flex flex-col gap-8 border-l-2 border-moon-200 pl-12">
          {reason && <span className="text-sm text-moon-700">{reason}</span>}
          {domainList.length > 0 && (
            <div className="flex flex-wrap gap-4">
              {domainList.map(domain => (
                <span
                  key={domain}
                  className="rounded bg-moon-100 px-6 py-1 text-xs font-[Inconsolata] text-moon-600">
                  {domain}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
